import { create } from 'zustand';
import {
  RestaurantNeeds,
  FetchNeedsIndexParams,
  fetchNeedsIndex,
} from '../services/waste/wasteNeedsService';

/**
 * All-restaurants waste "needed ingredients" store (New UI).
 *
 * Holds the PERIOD-INDEPENDENT per-unit needs index per restaurant (from the server's
 * `/needs-index`). Recipes change rarely, so this is fetched ONCE per restaurant set and
 * reused for every period — the Dashboard computes waste for the selected period (and any
 * other) client-side from these needs + already-loaded orders/revenue, with no further
 * server calls when stepping the time controls.
 */
function cacheKey(params: FetchNeedsIndexParams): string {
  return params.restaurantIds ?? 'all';
}

interface WasteNeedsState {
  /** Per-unit needs by restaurantId. */
  needs: Record<string, RestaurantNeeds>;
  isLoading: boolean;
  /** Key of the currently-held response; null until the first successful load. */
  loadedKey: string | null;
  load: (params?: FetchNeedsIndexParams) => Promise<void>;
  invalidate: (restaurantId: string) => void;
  reset: () => void;
}

let requestGeneration = 0;
const pendingRefresh = new Set<string>();

export const useWasteNeedsStore = create<WasteNeedsState>((set, get) => ({
  needs: {},
  isLoading: false,
  loadedKey: null,

  load: async (params: FetchNeedsIndexParams = {}) => {
    const key = cacheKey(params);
    // Already holding this exact set (and not mid-flight) → reuse it.
    if (get().loadedKey === key && !get().isLoading && !params.refreshRestaurantIds?.length) return;

    const generation = ++requestGeneration;
    const requestedIds = key === 'all' ? null : new Set(key.split(',').map(id => id.trim()));
    const refreshRestaurantIds = Array.from(new Set([
      ...(params.refreshRestaurantIds ?? []),
      ...Array.from(pendingRefresh).filter(id => !requestedIds || requestedIds.has(id)),
    ]));
    set({ isLoading: true });
    try {
      const rows = await fetchNeedsIndex({ ...params, refreshRestaurantIds });
      if (generation !== requestGeneration) return;
      const needs: Record<string, RestaurantNeeds> = {};
      for (const r of rows) needs[r.restaurantId] = r;
      for (const id of refreshRestaurantIds) {
        if (needs[id]) pendingRefresh.delete(id);
      }
      const missingRefresh = refreshRestaurantIds.some(id => !needs[id]);
      set({ needs, loadedKey: missingRefresh ? null : key, isLoading: false });
    } catch (err) {
      if (generation !== requestGeneration) return;
      console.error('[ERROR] loading waste needs index', err);
      set({ isLoading: false });
    }
  },

  invalidate: (restaurantId) => {
    requestGeneration++;
    pendingRefresh.add(restaurantId);
    const needs = { ...get().needs };
    delete needs[restaurantId];
    set({ needs, isLoading: false, loadedKey: null });
  },

  reset: () => {
    requestGeneration++;
    pendingRefresh.clear();
    set({ needs: {}, isLoading: false, loadedKey: null });
  },
}));
